import {
  SHIPPING_COUNTRIES,
  mergeCountryRates,
  shippingCountryLabel,
  type ShippingCountryRate,
} from "@gaole/shared";
import { Trash2 } from "lucide-react";
import { Button, NumberField, Select } from "../../components/ui";

export function CountryRatesEditor({
  value,
  fallbackFee,
  onChange,
}: {
  value: unknown;
  fallbackFee: number;
  onChange: (rates: ShippingCountryRate[]) => void;
}) {
  const rates = mergeCountryRates(value);
  const usedCodes = new Set(rates.map((r) => r.country));
  const available = SHIPPING_COUNTRIES.filter((c) => c.code !== "MY" && !usedCodes.has(c.code));

  function setFee(country: string, fee: number) {
    onChange(rates.map((row) => (row.country === country ? { ...row, fee } : row)));
  }

  function addCountry(code: string) {
    if (!code || usedCodes.has(code)) return;
    onChange([...rates, { country: code, fee: fallbackFee }].sort((a, b) => a.country.localeCompare(b.country)));
  }

  function removeCountry(country: string) {
    onChange(rates.filter((row) => row.country !== country));
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        Malaysia uses per-state rates below. Set a flat fee for each international country; any country not listed uses
        the default fee for other countries.
      </p>
      <div className="overflow-hidden rounded-xl border border-line">
        <div className="grid grid-cols-[minmax(0,1fr)_120px_40px] gap-3 border-b border-line bg-surface-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted">
          <span>Country</span>
          <span className="text-right">Fee (MYR)</span>
          <span />
        </div>
        {rates.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted">No international country rates yet.</div>
        ) : (
          <div className="max-h-[320px] overflow-y-auto">
            {rates.map((row) => (
              <div
                key={row.country}
                className="grid grid-cols-[minmax(0,1fr)_120px_40px] items-center gap-3 border-b border-line/60 px-3 py-2 last:border-b-0"
              >
                <span className="text-sm text-foreground">{shippingCountryLabel(row.country)}</span>
                <NumberField
                  decimal
                  min={0}
                  step="0.01"
                  className="text-right"
                  value={row.fee}
                  onChange={(fee) => setFee(row.country, fee)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  onClick={() => removeCountry(row.country)}
                  aria-label={`Remove ${shippingCountryLabel(row.country)}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      {available.length > 0 && (
        <Select
          label="Add country"
          value=""
          onChange={(e) => {
            if (e.target.value) addCountry(e.target.value);
          }}
          className="max-w-xs"
        >
          <option value="">Select country…</option>
          {available.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}
